import { useEffect, useRef } from "react";

import type { BlsState } from "../domain/blsChannel";
import { edgesCrossed, type BlsEdge } from "../domain/blsMotion";

type Options = {
    state: BlsState | null;
    cyclesNow: (nowEpochMs?: number) => number;
    /** Whether to listen at all. Nothing runs while false. */
    active: boolean;
    onEdge: (edge: BlsEdge) => void;
};

/**
 * Calls `onEdge` each time the dot reaches an edge of a running set.
 *
 * Shared by everything that marks the edges — the tones and the controllers'
 * pulses — so they land on the same frame. Each frame asks how far the set
 * has got; after a gap (a tab in the background) only the latest edge fires,
 * never the backlog, which would arrive as a burst.
 */
export function useBlsEdges({ state, cyclesNow, active, onEdge }: Options): void {
    const onEdgeRef = useRef(onEdge);
    useEffect(() => {
        onEdgeRef.current = onEdge;
    }, [onEdge]);

    const running = active && state !== null && state.running;

    useEffect(() => {
        if (!running) return;
        let last = cyclesNow();
        let handle = requestAnimationFrame(function tick() {
            const now = cyclesNow();
            const edge = edgesCrossed(last, now).at(-1);
            last = now;
            if (edge) onEdgeRef.current(edge);
            handle = requestAnimationFrame(tick);
        });
        return () => cancelAnimationFrame(handle);
    }, [running, cyclesNow, state]);
}
